import React, { FC, lazy } from 'react';
import { Navigate, Route, Routes as SwitchRoutes } from 'react-router-dom';
import { Routes, RouteParts } from 'constants/route.constants';
import CustomRoute from 'modules/authentication/containers/CustomRoute/CustomRoute';
import { PermissionLevels } from 'common/constants/permissions';
import { FRONT_DESK_BREADCRUMB, HOME_BREADCRUMB } from 'constants/breadcrumb.constants';
import BreadcrumbContextProvider from 'common/components/BreadcrumbContextProvider/BreadcrumbContextProvider';
import {
  CookiePolicyContainer,
  PrivacyPolicyContainer,
  TermsOfServiceContainer,
} from 'modules/privacy-policy/containers';

const HomePageRoute = lazy(() => import('routes/HomePage/HomePage'));
const KidZoneRoutes = lazy(() => import('modules/kid-zone/routes'));
const FrontDeskRoutes = lazy(() => import('modules/front-desk/FrontDesk'));
const BookingRoutes = lazy(() => import('modules/booking/routes'));
const EmployeesRoutes = lazy(() => import('modules/employees/routes'));
const PeakEmployeesRoutes = lazy(() => import('modules/peakEmployees/routes'));
const CrmRoutes = lazy(() => import('modules/crm/routes/index'));
const PersonalTrainingCrmRoutes = lazy(() => import('modules/personal-training-crm/routes/index'));
const ReportsRoutes = lazy(() => import('modules/reports/routes'));
const PosSettingsRoutes = lazy(() => import('modules/pos-settings/routes'));
const PosKioskRoutes = lazy(() => import('modules/pos-kiosk/routes'));
const DictionariesRoutes = lazy(() => import('modules/dictionaries/routes'));
const ServicesRoutes = lazy(() => import('modules/services/routes'));
const DaySheetRoutes = lazy(() => import('modules/day-sheet/routes'));
const MembersRoutes = lazy(() => import('modules/members/routes'));
const PermissionsRoutes = lazy(() => import('modules/permissions/routes'));
const CorporationsRoutes = lazy(() => import('modules/corporations/routes'));
const CorporateSettingsRoutes = lazy(() => import('modules/corporate-settings/routes'));
const ClubsRoutes = lazy(() => import('modules/clubs/routes'));
const ClubSettingsRoutes = lazy(() => import('modules/club-settings/routes'));
const MyProfileRoutes = lazy(() => import('modules/my-profile/routes'));
const PeakSettingsRoutes = lazy(() => import('modules/peak-settings/routes'));
const TimeclockRoutes = lazy(() => import('modules/timeclock/routes'));
const CommunicationRoutes = lazy(() => import('modules/communication/routes'));
const RemittanceRoutes = lazy(() => import('modules/remittance/routes'));
const ClientAdjustmentsRoutes = lazy(() => import('modules/clientAdjustment/routes'));
const PeakClientAdjustmentsRoutes = lazy(() => import('modules/peakClientAdjustment/routes'));
const ReconsiliationRoutes = lazy(() => import('modules/reconsiliation/routes'));
const PeakReportsRoutes = lazy(() => import('modules/peakReports/routes'));
const CatchAllRoute = lazy(() => import('../CatchAll'));

const ClientRoutes: FC = () => {
  return (
    <SwitchRoutes>
      <Route path={Routes.SIGN_IN} element={<Navigate to={Routes.HOME} />} />

      <Route
        path={Routes.PRIVACY_POLICY}
        element={
          <CustomRoute
            secure
            levels={[PermissionLevels.PEAK, PermissionLevels.CORPORATE, PermissionLevels.CLIENT]}
          >
            <PrivacyPolicyContainer />
          </CustomRoute>
        }
      />

      <Route
        path={Routes.COOKIE_POLICY}
        element={
          <CustomRoute
            secure
            levels={[PermissionLevels.PEAK, PermissionLevels.CORPORATE, PermissionLevels.CLIENT]}
          >
            <CookiePolicyContainer />
          </CustomRoute>
        }
      />

      <Route
        path={Routes.TERMS_OF_SERVICE}
        element={
          <CustomRoute
            secure
            levels={[PermissionLevels.PEAK, PermissionLevels.CORPORATE, PermissionLevels.CLIENT]}
          >
            <TermsOfServiceContainer />
          </CustomRoute>
        }
      />

      <Route
        path={Routes.HOME}
        element={
          <CustomRoute
            secure
            levels={[PermissionLevels.PEAK, PermissionLevels.CORPORATE, PermissionLevels.CLIENT]}
          >
            <BreadcrumbContextProvider key="home" breadcrumbs={[HOME_BREADCRUMB]}>
              <HomePageRoute />
            </BreadcrumbContextProvider>
          </CustomRoute>
        }
      />

      <Route
        path={Routes.FRONT_DESK}
        element={
          <CustomRoute secure levels={[PermissionLevels.CLIENT]}>
            <BreadcrumbContextProvider
              key="frontDesk"
              breadcrumbs={[HOME_BREADCRUMB, FRONT_DESK_BREADCRUMB]}
            >
              <FrontDeskRoutes />
            </BreadcrumbContextProvider>
          </CustomRoute>
        }
      />

      <Route path={`${RouteParts.KID_ZONE}/*`} element={<KidZoneRoutes />} />
      <Route path={`${RouteParts.BOOKING}/*`} element={<BookingRoutes />} />
      <Route path={`${RouteParts.EMPLOYEES}/*`} element={<EmployeesRoutes />} />
      <Route path={`${RouteParts.PEAK_EMPLOYEES}/*`} element={<PeakEmployeesRoutes />} />
      <Route path={`${RouteParts.CRM}/*`} element={<CrmRoutes />} />
      <Route path={`${RouteParts.PT_CRM}/*`} element={<PersonalTrainingCrmRoutes />} />
      <Route path={`${RouteParts.REPORTS}/*`} element={<ReportsRoutes />} />
      <Route path={`${RouteParts.POS_CONTENT}/*`} element={<PosSettingsRoutes />} />
      <Route path={`${RouteParts.POS}/*`} element={<PosKioskRoutes />} />
      <Route path={`${RouteParts.DICTIONARIES}/*`} element={<DictionariesRoutes />} />
      <Route path={`${RouteParts.SERVICES_ROOT}/*`} element={<ServicesRoutes />} />
      <Route path={`${RouteParts.DAY_SHEET}/*`} element={<DaySheetRoutes />} />
      <Route path={`${RouteParts.MEMBERS}/*`} element={<MembersRoutes />} />
      <Route path={`${RouteParts.PERMISSIONS}/*`} element={<PermissionsRoutes />} />
      <Route path={`${RouteParts.CORPORATIONS}/*`} element={<CorporationsRoutes />} />
      <Route path={`${RouteParts.CORPORATE_SETTINGS}/*`} element={<CorporateSettingsRoutes />} />
      <Route path={`${RouteParts.CLUBS}/*`} element={<ClubsRoutes />} />
      <Route path={`${RouteParts.CLUB_SETTINGS}/*`} element={<ClubSettingsRoutes />} />
      <Route path={`${RouteParts.MY_PROFILE}/*`} element={<MyProfileRoutes />} />
      <Route path={`${RouteParts.PEAK_SETTINGS}/*`} element={<PeakSettingsRoutes />} />
      <Route path={`${RouteParts.TIMECLOCK}/*`} element={<TimeclockRoutes />} />
      <Route path={`${RouteParts.COMMUNICATION}/*`} element={<CommunicationRoutes />} />
      <Route path={`${RouteParts.PEAK_REMITTANCE}/*`} element={<RemittanceRoutes />} />
      <Route path={`${RouteParts.CLIENT_ADJUSTMENTS}/*`} element={<ClientAdjustmentsRoutes />} />
      <Route
        path={`${RouteParts.PEAK_CLIENT_ADJUSTMENTS}/*`}
        element={<PeakClientAdjustmentsRoutes />}
      />
      <Route path={`${RouteParts.PEAK_REPORTS}/*`} element={<PeakReportsRoutes />} />
      <Route path={`${RouteParts.DEPOSIT_RECONCILIATION}/*`} element={<ReconsiliationRoutes />} />
      <Route path="*" element={<CatchAllRoute />} />
    </SwitchRoutes>
  );
};

export default ClientRoutes;
